import React, { useState, useEffect } from "react"
import _ from "lodash"

import { MultiRowCarouselState_s, SingleRowCarouselState_s } from "components/table/carousel/carousel-styles"
import { DateInput, DropdownInput, NumberInput } from "components/table/carousel/inputs"
import { field_attrs } from "components/table/lib/column-field"
import RowAddRemove from "components/table/carousel/row-add-remove"

import { Button_s } from "styles/app-styles"
import { ProRataRecovery_s } from "components/recoveries/named-recoveries/named-recoveries-styles"
import { isValidDate } from "data/lib/dates"

const ProRataRecovery = (props: {rowData: [], namedItems: any, field: string, persist: Function}) => {

  const emptyRecord = {
    recoveryPoolNamedItemId: null,
    proRataType: 'Natural',
    proRataPercent: null,
    areaMeasureNamedItemId: _.find(props.namedItems?.areaMeasures, {code: '*TotalArea'})?.areaid,
    stop: null,
    stopType: 'None',
    reportTagNamedItemId: null,
    cap: null
  }
  const emptyItem = {
    date: null,
    amt: null
  }
  type Item_t = object & {
    i: number
  }

  const [data, setData] = useState<object|null>(props.rowData['recoverydetail']);
  const initialItems = props.rowData['recoverydetail']?.['cap']?.['details'];
  const [items, setItems] = useState<Item_t[]>(initialItems ? _.sortBy(initialItems.map((item, i) => { return {...item, i }})) : [{...emptyItem, i: 0}]);

  useEffect(() => {
    if (!props['rowData'] || props['rowData']['recoverydetail']['state'] !== 'proRataRecovery') setData(emptyRecord)
  }, [props['rowData']])

  function updateData(val:any, field:string):void {
    let nextData = _.cloneDeep(data)
    // Detailed cap key
     if (Array.isArray(field)){
      if (!nextData['cap']) nextData['cap'] = {capOn: 'Pool'}
      nextData[field[0]][field[1]] = val
    // Unnested recovery detail key
    } else {
      nextData[field] = val
    }
    setData(nextData);
    if (field === 'proRataType'){
      nextData['proRataPercent'] = null
      nextData['areaMeasureNamedItemId'] = null
    }
    if (field === 'stopType' && (val === 'None' || val === 'Occupancy Yr' || val === 'Occupancy Yr+1' || val === 'Occupancy Yr-1')){
      nextData['stop'] = null
    }
    if (field[1] === 'capType'){
      if (val.startsWith('Detailed')){
        delete nextData['cap']['value']
        delete nextData['cap']['increasePercent']
      } else {
        delete nextData['cap']['details']
      }
    }
    props.persist({...nextData, state: 'proRataRecovery'}, 'recoverydetail', props.rowData)
  }

  function updateCap(val:any, field:string):void {
    let nextItems = _.cloneDeep(items)
    nextItems[Number(field[0])][field[1]] = val
    setItems(nextItems)

    let nextData = _.cloneDeep(data)
    let idx = Number(field[0])
    if (!nextData['cap']['details']) nextData['cap']['details'] = [emptyItem]
    if (!nextData['cap']['details'][idx]){
      nextData['cap']['details'][idx] = {}
    }
    nextData['cap']['details'][idx][field[1]] = val
    if (field[1] === 'date'){
      isValidDate(val) ? nextData['cap']['dateMode'] = 'absolute' : nextData['cap']['dateMode'] = 'relative'
    }
    setData(nextData);

    props.persist({...nextData, state: 'proRataRecovery'}, 'recoverydetail', props.rowData)
  }

  function updateItems(items:Item_t[]):void {
    setItems(items);
    let data = _.cloneDeep(props.rowData[props.field]['cap']);
    const strippedItems = _.sortBy(items.map(({i, ...rest}) => rest));
    _.set(data, 'details', strippedItems);
    props.persist(data, 'recoverydetail.cap', props.rowData);
  }

  const recoveryPoolOptions = props.namedItems.recoveryPools?.map(pool => {
    if (pool.code) return {label: pool.code, value: pool.recoverypoolid}
  })
  const areaOptions = props.namedItems.areaMeasures?.map(area => {
    if (area.code) return {label: area.code, value: area.areaid}
  })
  const reportTagOptions = props.namedItems.reportTags?.map(tag => {
    if (tag.code) return {label: tag.code, value: tag.reporttagid}
  })

  const attrs = (field) => field_attrs('recovery', 'recoverydetail')(field)(props.rowData, props)
  const item_attrs = (field) => field_attrs('recovery', `recoverydetail.cap.details.${props.field}`)(field)(props.rowData, props)
  const capDetail = data?.['cap']?.['capType']?.startsWith('Detailed')
  let renderItems = _.sortBy(items, 'i');

  return (
    <ProRataRecovery_s cap={data?.['cap'] && Object.keys(data?.['cap']).length !== 0}>
      <SingleRowCarouselState_s>
        <div>
          <label>Recovery Pool</label>
          <DropdownInput
            className="automated reporttag"
            data={data}
            attrs={attrs}
            field={'recoveryPoolNamedItemId'}
            options={recoveryPoolOptions}
            persist={updateData}
          />
        </div>
        <div>
          <label>Pro Rata Type</label>
          <DropdownInput
            className="automated"
            data={data}
            attrs={attrs}
            field={'proRataType'}
            options={['Natural', 'Specified %']}
            persist={updateData}
          />
        </div>
        <div className="short-input">
          <label>Pro Rata %</label>
          <NumberInput
            className="automated"
            data={data}
            disabled={data?.['proRataType'] === 'Natural'}
            disabledText="N/A"
            attrs={attrs}
            field="proRataPercent"
            readPrecision={4}
            writePrecision={4}
            min={0}
            max={100}
            persist={updateData}
          />
        </div>
        <div>
          <label>Denominator</label>
          <DropdownInput
            className="automated denominator"
            data={data}
            disabled={data?.['proRataType'] !== 'Natural'}
            disabledText="N/A"
            attrs={attrs}
            field={'areaMeasureNamedItemId'}
            options={areaOptions}
            persist={updateData}
          />
        </div>
        <div>
          <label>Stop Type</label>
          <DropdownInput
            className="automated stop-type"
            data={data}
            disabledText="N/A"
            attrs={attrs}
            field={'stopType'}
            options={['None', 'Amt', 'Amt/Area', 'Occupancy Yr', 'Occupancy Yr+1', 'Occupancy Yr-1', 'Specified Yr', 'Est. Base Yr']}
            persist={updateData}
          />
        </div>
        <div>
          <label>Stop</label>
          <NumberInput
            className="automated billion-input"
            data={data}
            disabled={data?.['stopType']?.startsWith('Occupancy') || data?.['stopType'] === 'None'}
            disabledText="N/A"
            attrs={attrs}
            field="stop"
            readPrecision={4}
            writePrecision={4}
            min={0}
            max={data?.['stopType'] === 'Est. Base Yr' ? 100 : 1000000000}
            persist={updateData}
          />
        </div>
        <div>
          <label>Report Tag</label>
          <DropdownInput
            className="automated reporttag"
            data={data}
            attrs={attrs}
            field={'reportTagNamedItemId'}
            options={reportTagOptions}
            persist={updateData}
            onClear={() => updateData(null, 'reportTagNamedItemId')}
          />
        </div>
      </SingleRowCarouselState_s>
      <SingleRowCarouselState_s>
        <div>
          <details>
            <summary>Cap</summary>
            <div className="clear-btn"><Button_s danger onClick={() => { updateData(null, 'cap'); setItems([{...emptyItem, i: 0}]) }}>Clear</Button_s></div>
            <SingleRowCarouselState_s>
              <div>
                <label>Cap Type</label>
                <DropdownInput
                  className="automated"
                  data={data}
                  attrs={attrs}
                  field={['cap', 'capType']}
                  options={['Percent', '/Area/Yr', '/Yr', 'Prior Yr', 'Detailed /Yr', 'Detailed /Area/Yr']}
                  persist={updateData}
                />
              </div>
              <div>
                <label>Cap On</label>
                <DropdownInput
                  className="automated"
                  data={data}
                  attrs={attrs}
                  field={['cap', 'capOn']}
                  options={['Pool', 'Recovery']}
                  persist={updateData}
                />
              </div>
              <div>
                <label>Amt</label>
                <NumberInput
                  className="automated billion-input"
                  data={data}
                  disabled={data?.['cap']?.['capType'] == 'Detailed /Yr' || data?.['cap']?.['capType'] == 'Detailed /Area/Yr'}
                  disabledText="N/A"
                  attrs={attrs}
                  field={['cap', 'value']}
                  readPrecision={4}
                  writePrecision={4}
                  min={0}
                  max={1000000000}
                  persist={updateData}
                />
              </div>
              <div className="short-input">
                <label>% Increase</label>
                <NumberInput
                  className="automated"
                  data={data}
                  disabled={data?.['cap']?.['capType'] == 'Percent' || data?.['cap']?.['capType'] == 'Detailed /Yr' || data?.['cap']?.['capType'] == 'Detailed /Area/Yr'}
                  disabledText="N/A"
                  attrs={attrs}
                  field={['cap', 'increasePercent']}
                  readPrecision={4}
                  writePrecision={4}
                  min={0}
                  max={100}
                  persist={updateData}
                />
              </div>
            </SingleRowCarouselState_s>
            { capDetail && renderItems.map((row: object, i) => (
              <MultiRowCarouselState_s className="carousel-multi-row">
                <div>
                  { i === 0 && <label>Date</label> }
                  <DateInput
                    data={items}
                    className="automated cap-date"
                    attrs={item_attrs}
                    field={[`${row['i']}`, 'date']}
                    format="MM/DD/YYYY"
                    allowRelative
                    persist={updateCap}
                  />
                </div>
                <div>
                  { i === 0 && <label>Amt</label> }
                  <NumberInput
                    allowRelative
                    data={items}
                    className="automated billion-input"
                    attrs={item_attrs}
                    field={[`${row['i']}`, 'amt']}
                    readPrecision={4}
                    writePrecision={4}
                    min={0}
                    max={1000000000}
                    persist={updateCap}
                  />
                </div>
                <RowAddRemove emptyRecord={emptyItem} setState={updateItems} row={i} items={items} />
              </MultiRowCarouselState_s>
          ))}
          </details>
        </div>
      </SingleRowCarouselState_s>
    </ProRataRecovery_s>
  )
}

export default ProRataRecovery

